import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo, memo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ChatMessageResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { sanitizeChatHtml } from '@/lib/sanitize-chat-html';
import { toast } from '@/components/ui/use-toast';
import { AttachmentItem } from './AttachmentItem';
import { UserProfileModal } from './UserProfileModal';
import { UserActionMenu } from './UserActionMenu';
import { chatHtmlToPlainText, chatHtmlToCopyText, postedViaLabel } from '../utils';
import { UserAvatar } from './UserAvatar';
import { useLongPress } from '../hooks/useLongPress';

// Long messages (e.g. a big paste) collapse to a max height with a "Show more"
// toggle, WhatsApp-style, so one wall of text can't flood the timeline. Trigger
// a bit above the collapsed height so the toggle only appears when it
// meaningfully hides content (text is 14px / 1.4 line-height ≈ 19.6px per line).
const COLLAPSE_TRIGGER_PX = 320;
const COLLAPSED_MAX_PX = 280;

const QUICK_REACTIONS = ['❤️', '👍', '👎', '🔥', '😂', '👏'];

interface MessageItemProps {
  message: ChatMessageResponse;
  isOwnMessage: boolean;
  /** The viewing user's id — used to highlight an @mention that targets them. */
  currentUserId?: string | null;
  isAdmin?: boolean;
  isConsecutive?: boolean;
  isLastInGroup?: boolean;
  onStartEdit?: (messageId: string) => void;
  onDelete?: (messageId: string) => void;
  onReply?: (messageId: string) => void;
  onReaction?: (messageId: string, emoji: string) => void;
  onOpenDm?: (userId: string) => void;
  /** Scroll the list to the message identified by `messageId` (used by reply previews). */
  onJumpToMessage?: (messageId: string) => void;
}

function RoleBadge({ role }: { role: string }) {
  const cls =
    role === 'admin'
      ? 'bg-green-100 text-green-700'
      : role === 'moderator'
        ? 'bg-blue-100 text-blue-700'
        : role === 'ambassador'
          ? 'bg-purple-100 text-purple-700'
          : 'bg-gray-100 text-gray-600';
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{role}</span>;
}

function ReplyPreview({
  preview,
  isOwnMessage,
  onClick,
}: {
  preview: { senderName: string; content: string };
  isOwnMessage: boolean;
  /** When provided, the preview becomes a button that jumps to the original message. */
  onClick?: () => void;
}) {
  // The stored content is rich-text HTML; render it as plain text so tags don't
  // leak into the preview, and let it wrap/clamp so a long quote can't push the
  // bubble past the viewport edge.
  const previewText = chatHtmlToPlainText(preview.content);
  const className = cn(
    'block w-full text-left mb-1.5 px-2.5 py-1.5 rounded-lg border-l-[3px] overflow-hidden',
    isOwnMessage ? 'bg-black/10 border-green-600' : 'bg-black/[0.07] border-indigo-400',
    onClick &&
      cn(
        'cursor-pointer transition-colors',
        isOwnMessage ? 'hover:bg-black/20' : 'hover:bg-black/[0.12]',
      ),
  );
  const inner = (
    <>
      <p className="text-[12px] font-semibold leading-tight truncate text-black/80">
        {preview.senderName}
      </p>
      <p className="text-[12px] text-black/60 break-words line-clamp-2">{previewText}</p>
    </>
  );

  if (!onClick) {
    return <div className={className}>{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      aria-label={`Jump to replied message from ${preview.senderName}`}
    >
      {inner}
    </button>
  );
}

function ReactionPills({
  reactions,
  isOwnMessage,
}: {
  reactions: Array<{ emoji: string; count: number; userIds: string[] }>;
  isOwnMessage: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {reactions.map((r) => (
        <span
          key={r.emoji}
          className={cn(
            'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[12px] font-medium text-black/70 cursor-pointer select-none transition-colors',
            isOwnMessage ? 'bg-black/15 hover:bg-black/20' : 'bg-black/10 hover:bg-black/[0.15]',
          )}
        >
          {r.count} {r.emoji}
        </span>
      ))}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  danger,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-[14px] py-2 text-[14px] transition-colors',
        danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50',
      )}
    >
      {label}
    </button>
  );
}

function ContextMenuPanel({
  x,
  y,
  isOwnMessage,
  canDelete,
  onReply,
  onStartEdit,
  onCopy,
  onReaction,
  onDelete,
  onMarkUnread,
  onClose,
}: {
  x: number;
  y: number;
  isOwnMessage: boolean;
  canDelete: boolean;
  onReply: () => void;
  onStartEdit: () => void;
  onCopy: () => void;
  onReaction: (emoji: string) => void;
  onDelete: () => void;
  onMarkUnread?: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{ left: x, top: y, WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
      // select-none: the long-press that opens this menu is also the gesture the
      // browser uses to start a text selection — without this, holding to open the
      // menu highlights the emoji row / Reply label and the buttons stop responding
      // (the tap is read as adjusting the selection instead of clicking).
      className="fixed z-50 min-w-[180px] rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.2)] bg-white py-1.5 select-none"
    >
      {/* Quick reactions row */}
      <div className="flex items-center justify-around px-3 py-2 border-b border-gray-100">
        {QUICK_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            onClick={() => {
              onReaction(emoji);
              onClose();
            }}
            className="text-xl hover:scale-125 transition-transform"
          >
            {emoji}
          </button>
        ))}
      </div>
      {/* Action items */}
      <div className="pt-1">
        <MenuItem
          label="Reply"
          onClick={() => {
            onReply();
            onClose();
          }}
        />
        {isOwnMessage && (
          <MenuItem
            label="Edit"
            onClick={() => {
              onStartEdit();
              onClose();
            }}
          />
        )}
        <MenuItem
          label="Copy Text"
          onClick={() => {
            onCopy();
            onClose();
          }}
        />
        {!isOwnMessage && onMarkUnread && (
          <MenuItem
            label="Mark as unread"
            onClick={() => {
              onMarkUnread();
              onClose();
            }}
          />
        )}
        {canDelete && (
          <MenuItem
            label="Delete"
            onClick={() => {
              onDelete();
              onClose();
            }}
            danger
          />
        )}
      </div>
    </div>
  );
}

/**
 * Memoized so a re-render of the message list (a new message, a reaction, a
 * typing tick) doesn't re-render every row. Items whose props are unchanged
 * keep their DOM intact — which is what lets an in-progress text selection
 * survive long enough to copy, instead of being wiped by an unrelated update.
 * `addMessage`/`updateMessage` preserve the object reference of untouched
 * messages, so a shallow prop comparison skips them. The parent passes
 * stable (useCallback) handlers.
 */
export const MessageItem = memo(MessageItemImpl);

function MessageItemImpl({
  message,
  isOwnMessage,
  currentUserId,
  isAdmin,
  isConsecutive,
  isLastInGroup = true,
  onStartEdit,
  onDelete,
  onReply,
  onReaction,
  onOpenDm,
  onJumpToMessage,
}: MessageItemProps) {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  // The user whose profile popup is open. Fed by two triggers: clicking an
  // @mention in message text, or clicking a sender's avatar.
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [timestampInline, setTimestampInline] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Click delegation — detect clicks on mention spans inside dangerouslySetInnerHTML content
  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest('[data-mention]');
    if (!el) return;
    const uid = el.getAttribute('data-user-id');
    if (uid) setProfileUserId(uid);
  }, []);

  const openMenuAt = useCallback((clientX: number, clientY: number) => {
    const x = Math.min(clientX, window.innerWidth - 200);
    const y = Math.min(clientY, window.innerHeight - 240);
    // Clear any selection the long-press started on the message bubble so it
    // doesn't bleed into the menu that's about to render under the finger.
    window.getSelection()?.removeAllRanges();
    setContextMenu({ x, y });
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openMenuAt(e.clientX, e.clientY);
    },
    [openMenuAt],
  );

  /**
   * Enter text-selection mode for this message (touch only).
   *
   * Message bubbles carry `pointer-coarse:select-none` because a plain
   * long-press has to open the context menu — a native selection starting under
   * the finger hijacks that gesture. The cost was that touch users could not
   * select message text at all, only copy the whole message from the menu.
   *
   * Double-tap-and-hold re-opens that door. Selection is re-enabled for this
   * one bubble and the text is selected outright, which is what makes it work:
   * flipping `user-select` mid-gesture does not retroactively start the
   * browser's own selection, but a programmatic Range gives the platform
   * selection handles, and from there drag-to-adjust and the system
   * Copy/Share menu behave natively.
   */
  const [selecting, setSelecting] = useState(false);
  const beginTextSelection = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    setSelecting(true);
    // Selection must be applied after the class flip lands, or the browser
    // drops the range on a still-unselectable node.
    requestAnimationFrame(() => {
      const selection = window.getSelection();
      if (!selection || !textRef.current) return;
      const range = document.createRange();
      range.selectNodeContents(textRef.current);
      selection.removeAllRanges();
      selection.addRange(range);
    });
  }, []);

  // Leave selection mode once the selection is gone (tap elsewhere, scroll
  // dismiss, Copy from the system menu) so the long-press menu comes back.
  useEffect(() => {
    if (!selecting) return;
    const onSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        setSelecting(false);
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [selecting]);

  // Touch devices have no right-click — a long press opens the same menu, and
  // a double-tap-and-hold selects the text instead.
  const longPressHandlers = useLongPress((x, y) => openMenuAt(x, y), {
    onDoubleTapHold: beginTextSelection,
  });

  const handleCopy = useCallback(() => {
    // Copy the rendered text, not the raw HTML — otherwise the clipboard gets
    // literal "<p>" tags and stray leading/trailing newlines ("paragraph
    // marks"). chatHtmlToCopyText strips tags, keeps internal line breaks, and
    // trims the ends.
    navigator.clipboard.writeText(chatHtmlToCopyText(message.content));
    toast({ description: 'Copied to clipboard.' });
  }, [message.content]);

  const queryClient = useQueryClient();
  const handleMarkUnread = useCallback(async () => {
    try {
      await api.markChatChannelUnread(message.channelId, message.messageId);
      // Recompute the channel list unread glyph and the cross-arche worktop /
      // header badge from the rolled-back read pointer.
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.channels() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.summary() });
      toast({ description: 'Marked as unread.' });
    } catch {
      toast({ description: 'Could not mark as unread.', variant: 'destructive' });
    }
  }, [message.channelId, message.messageId, queryClient]);

  const hasText = !!(message.content && message.content.trim().length > 0);
  const hasNonText = !!(message.replyPreview || (message.attachmentRefs?.length ?? 0) > 0);

  useLayoutEffect(() => {
    if (hasNonText || !hasText || !textRef.current) {
      setTimestampInline(false);
      return;
    }
    const el = textRef.current;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
    setTimestampInline(el.scrollHeight <= Math.ceil(lineHeight) + 2);
  }, [message.content, hasNonText, hasText]);

  // Decide whether the message is long enough to collapse. scrollHeight is the
  // full content height regardless of the max-height clamp, so this is stable
  // across expand/collapse and needs no remeasure on toggle.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!hasText || !el) {
      setOverflowing(false);
      return;
    }
    setOverflowing(el.scrollHeight > COLLAPSE_TRIGGER_PX);
  }, [message.content, hasText]);

  if (message.isDeleted) {
    return <div className="py-1 px-4 text-sm text-muted-foreground/50 italic">Message deleted</div>;
  }

  if (message.messageType === 'system') {
    return (
      <div className="flex items-center justify-center py-2 px-4">
        <p className="text-xs text-muted-foreground italic">{message.content}</p>
      </div>
    );
  }

  const senderInitial = (message.senderName || 'U').charAt(0).toUpperCase();
  const displayName = message.senderName || 'User';
  const timestamp = new Date(message.createdAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  // An app-posted message says so beside the time ("via LitCraft Chat"), so
  // readers can tell it from one its author typed.
  const timestampLabel = [timestamp, postedViaLabel(message.metadata), message.isEdited && 'edited']
    .filter(Boolean)
    .join(' · ');
  const canDelete = isOwnMessage || !!isAdmin;
  const outerMargin = isConsecutive ? 'mt-1' : 'mt-3';

  const avatarEl = (
    <button
      type="button"
      // stopPropagation so a tap opens the profile instead of bubbling to the
      // row's context-menu / long-press handlers.
      onClick={(e) => {
        e.stopPropagation();
        setProfileUserId(message.senderId);
      }}
      className="block rounded-full transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      aria-label={`View ${displayName}'s profile`}
    >
      <UserAvatar userId={message.senderId} displayName={displayName} initial={senderInitial} />
    </button>
  );

  const replyPreviewEl = message.replyPreview ? (
    <ReplyPreview
      preview={message.replyPreview}
      isOwnMessage={isOwnMessage}
      onClick={
        message.replyToId && onJumpToMessage ? () => onJumpToMessage(message.replyToId!) : undefined
      }
    />
  ) : null;

  const sanitizedContent = useMemo(
    () => (hasText ? sanitizeChatHtml(message.content) : ''),
    [hasText, message.content],
  );

  // Mark mentions of the viewing user so they stand out in-thread (Slack-style).
  // Done as a DOM pass over the rendered content (same `[data-mention]` selector
  // the click-delegation uses) rather than string-munging the HTML — robust to
  // markup/attribute-order changes and consistent with handleContentClick.
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.querySelectorAll<HTMLElement>('[data-mention]').forEach((span) => {
      if (currentUserId && span.getAttribute('data-user-id') === currentUserId) {
        span.setAttribute('data-mention-self', 'true');
      } else {
        span.removeAttribute('data-mention-self');
      }
    });
  }, [sanitizedContent, currentUserId]);

  const collapsed = overflowing && !expanded;
  const textEl = hasText ? (
    <div>
      <div
        ref={textRef}
        className={cn(
          'chat-message-content text-[14px] leading-[1.4] text-[#111] break-words',
          collapsed && 'overflow-hidden',
        )}
        style={collapsed ? { maxHeight: COLLAPSED_MAX_PX } : undefined}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        onClick={handleContentClick}
      />
      {overflowing && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-0.5 text-[13px] font-medium text-blue-600 hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  ) : null;

  const attachmentsEl =
    message.attachmentRefs && message.attachmentRefs.length > 0 ? (
      <div className="flex flex-col gap-1">
        {message.attachmentRefs.map((ref) => (
          <AttachmentItem
            key={ref.fileId}
            attachment={ref}
            isOwnMessage={isOwnMessage}
            channelId={message.channelId}
          />
        ))}
      </div>
    ) : null;

  const timestampEl = !timestampInline ? (
    <span className="absolute bottom-1.5 right-2.5 text-[11px] text-black/40 pointer-events-none select-none whitespace-nowrap">
      {timestampLabel}
    </span>
  ) : null;

  const inlineTimestampEl = timestampInline ? (
    <span className="text-[11px] text-black/40 pointer-events-none select-none whitespace-nowrap flex-shrink-0 self-end mb-px">
      {timestampLabel}
    </span>
  ) : null;

  const reactionsEl =
    message.reactions && message.reactions.length > 0 ? (
      <ReactionPills reactions={message.reactions} isOwnMessage={isOwnMessage} />
    ) : null;

  const contextMenuEl = contextMenu && (
    <ContextMenuPanel
      x={contextMenu.x}
      y={contextMenu.y}
      isOwnMessage={isOwnMessage}
      canDelete={canDelete}
      onReply={() => onReply?.(message.messageId)}
      onStartEdit={() => onStartEdit?.(message.messageId)}
      onCopy={handleCopy}
      onReaction={(emoji) => onReaction?.(message.messageId, emoji)}
      onDelete={() => onDelete?.(message.messageId)}
      onMarkUnread={handleMarkUnread}
      onClose={() => setContextMenu(null)}
    />
  );

  const profileModalEl = profileUserId && (
    <UserProfileModal
      userId={profileUserId}
      onClose={() => setProfileUserId(null)}
      onOpenDm={
        onOpenDm
          ? (uid) => {
              onOpenDm(uid);
            }
          : undefined
      }
    />
  );

  if (isOwnMessage) {
    return (
      <>
        <div
          // pointer-coarse:select-none — on touch devices a long-press must not
          // start a native text selection (that's what hijacks the context menu);
          // desktop keeps normal mouse selection for copying. Double-tap-and-hold
          // lifts it for this bubble (see beginTextSelection).
          className={cn(
            'flex justify-end items-end gap-2 px-4',
            selecting ? 'select-text' : 'pointer-coarse:select-none',
            outerMargin,
          )}
          style={selecting ? undefined : { WebkitTouchCallout: 'none' }}
          onContextMenu={handleContextMenu}
          {...longPressHandlers}
        >
          {/* min-w-0: a flex item's automatic minimum size is its MIN-CONTENT
              width, and that beats max-width — so without this a bubble holding
              an unbreakable run (pasted terminal output) ignores the 70% cap and
              pushes the message off-screen. */}
          <div className="flex flex-col items-end max-w-[70%] min-w-0">
            <div
              className={cn(
                'relative bg-[#cfe8b4] px-[14px] pt-[10px] min-w-[80px] max-w-full',
                timestampInline ? 'pb-[10px]' : 'pb-5',
              )}
              style={{ borderRadius: isLastInGroup ? '16px 6px 16px 16px' : '16px 6px 6px 16px' }}
            >
              {replyPreviewEl}
              {timestampInline ? (
                <div className="flex items-end gap-1.5">
                  <div className="flex-1 min-w-0">{textEl}</div>
                  {inlineTimestampEl}
                </div>
              ) : (
                textEl
              )}
              {attachmentsEl}
              {reactionsEl}
              {timestampEl}
            </div>
          </div>
          <div className="w-9 flex-shrink-0">{!isConsecutive && avatarEl}</div>
          {contextMenuEl}
        </div>
        {profileModalEl}
      </>
    );
  }

  return (
    <>
      <div
        // pointer-coarse:select-none — see the own-message branch above.
        className={cn(
          'flex items-end gap-2 px-4',
          selecting ? 'select-text' : 'pointer-coarse:select-none',
          outerMargin,
        )}
        style={selecting ? undefined : { WebkitTouchCallout: 'none' }}
        onContextMenu={handleContextMenu}
        {...longPressHandlers}
      >
        <div className="w-9 flex-shrink-0">{!isConsecutive && avatarEl}</div>
        {/* min-w-0 — see the own-message branch above. */}
        <div className="flex flex-col max-w-[70%] min-w-0">
          {!isConsecutive && message.senderRole && message.senderRole !== 'user' && (
            <div className="flex items-center gap-1.5 mb-1">
              <RoleBadge role={message.senderRole} />
            </div>
          )}
          <div
            className={cn(
              'relative bg-[#f1f1f1] px-[14px] pt-[10px] min-w-[80px] max-w-full',
              timestampInline ? 'pb-[10px]' : 'pb-5',
            )}
            style={{ borderRadius: isLastInGroup ? '6px 16px 16px 16px' : '6px 16px 16px 6px' }}
          >
            {/* The sender's name is the standard person menu — the same one the
                channel roster and the guild member list show, so blocking or
                reporting someone is one act with one set of affordances
                wherever you meet them. The AVATAR still opens the profile card
                directly; that tap target is unchanged. */}
            <UserActionMenu
              userId={message.senderId}
              displayName={displayName}
              currentUserId={currentUserId}
              channelId={message.channelId}
              messageId={message.messageId}
              onMessage={onOpenDm}
            >
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                className="block text-[11px] text-black/60 mb-1 leading-none hover:underline text-left"
                aria-label={`Options for ${displayName}`}
              >
                {displayName}
              </button>
            </UserActionMenu>
            {replyPreviewEl}
            {timestampInline ? (
              <div className="flex items-end gap-1.5">
                <div className="flex-1 min-w-0">{textEl}</div>
                {inlineTimestampEl}
              </div>
            ) : (
              textEl
            )}
            {attachmentsEl}
            {reactionsEl}
            {timestampEl}
          </div>
        </div>
        {contextMenuEl}
      </div>
      {profileModalEl}
    </>
  );
}
